import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, X, Check, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import useSeo from "@/lib/useSeo";
import { useShop } from "@/context/ShopContext";
import { inr, COMBO } from "@/lib/content";

const inStock = (p) => p.sizes.filter((s) => p.stock[s] > 0);
const makePick = (p) => ({ p, size: inStock(p).includes("M") ? "M" : inStock(p)[0], color: p.colors[0].name });

const Slot = ({ pick, i, onChange, onRemove }) => {
  if (!pick) return (
    <div data-testid={`combo-slot-${i}-empty`} className="flex aspect-[3/4] flex-col items-center justify-center rounded-3xl border-2 border-dashed border-[#D99B8E] bg-[#F4EFEA] p-6 text-center">
      <span className="rounded-full bg-white p-3 text-[#B85D43]"><Plus className="h-6 w-6" /></span>
      <p className="mt-4 font-serif-display text-2xl">Style {i + 1}</p>
      <p className="mt-1 text-xs text-[#78716C]">Pick any ₹{COMBO.item} style below</p>
    </div>
  );
  const { p, size, color } = pick;
  return (
    <div data-testid={`combo-slot-${i}`} className="overflow-hidden rounded-3xl border border-[#E7E2DB] bg-white">
      <div className="relative">
        <img src={p.images[0]} alt={p.alt} className="aspect-[4/5] w-full object-cover object-top" />
        <button data-testid={`combo-slot-${i}-remove`} onClick={onRemove} aria-label="Remove" className="absolute right-3 top-3 rounded-full bg-white/90 p-2 shadow"><X className="h-4 w-4" /></button>
      </div>
      <div className="space-y-3 p-4">
        <p className="line-clamp-2 text-sm font-medium leading-snug">{p.name}</p>
        <div className="flex flex-wrap gap-1.5">
          {inStock(p).map((s) => (
            <button key={s} data-testid={`combo-slot-${i}-size-${s.replace(/\s+/g, "-")}`} onClick={() => onChange({ ...pick, size: s })} className={`rounded-full border px-3 py-1 text-xs ${size === s ? "border-[#701A24] bg-[#701A24] text-white" : "border-[#E7E2DB]"}`}>{s}</button>
          ))}
        </div>
        <div className="flex gap-2">
          {p.colors.map((c) => (
            <button key={c.name} title={c.name} aria-label={c.name} onClick={() => onChange({ ...pick, color: c.name })} style={{ background: c.hex }} className={`h-6 w-6 rounded-full border border-black/10 ring-offset-2 ${color === c.name ? "ring-2 ring-[#701A24]" : ""}`} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default function ComboBuilder() {
  useSeo("Build Your 2 for ₹329 Combo", "Pick any two Kurtaani ₹299 festive styles – chaniya cholis, lehengas, saree and Anarkali – and get both for just ₹329.");
  const { addToCart, setCartOpen } = useShop();
  const [items, setItems] = useState([]);
  const [picks, setPicks] = useState([null, null]);

  useEffect(() => {
    api.get("/products", { params: { min_price: COMBO.item, max_price: COMBO.item, limit: 50 } }).then((r) => setItems(r.data.filter((p) => inStock(p).length)));
  }, []);

  const pick = (p) => {
    const idx = picks.findIndex((x) => !x);
    if (idx === -1) return toast("Both slots are filled – remove one to swap");
    setPicks(picks.map((x, i) => (i === idx ? makePick(p) : x)));
  };
  const setAt = (i, v) => setPicks(picks.map((x, j) => (j === i ? v : x)));
  const ready = picks.every(Boolean);
  const addCombo = () => {
    picks.forEach(({ p, size, color }) => addToCart(p, size, color, 1, false));
    setPicks([null, null]);
    setCartOpen(true);
  };

  return (
    <div data-testid="combo-builder-page" className="kt-container py-12 sm:py-16">
      <p className="eyebrow">Combo offer</p>
      <h1 className="mt-3 font-serif-display text-5xl font-semibold leading-tight sm:text-6xl">Build your <em className="text-[#701A24]">2 for {inr(COMBO.pair)}</em> combo</h1>
      <p className="mt-4 max-w-2xl text-[#57534E]">Choose any two ₹{COMBO.item} styles – mix a chaniya choli with a lehenga, or pick the same style twice. The combo price is applied automatically at checkout.</p>

      <div className="mt-10 grid items-start gap-4 sm:gap-6 lg:grid-cols-12">
        <div className="grid grid-cols-2 gap-3 sm:gap-6 lg:col-span-8">
          {picks.map((x, i) => <Slot key={i} i={i} pick={x} onChange={(v) => setAt(i, v)} onRemove={() => setAt(i, null)} />)}
        </div>
        <aside className="rounded-3xl bg-[#701A24] p-6 text-[#FAF7F2] sm:p-8 lg:sticky lg:top-24 lg:col-span-4">
          <p className="text-xs uppercase tracking-[0.2em] text-[#D99B8E]">Your combo</p>
          <div className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between"><span>2 × {inr(COMBO.item)}</span><span className="line-through opacity-70">{inr(COMBO.item * 2)}</span></div>
            <div className="flex justify-between"><span>Combo saving</span><span>− {inr(COMBO.item * 2 - COMBO.pair)}</span></div>
          </div>
          <div className="mt-4 flex items-end justify-between border-t border-white/20 pt-4">
            <span className="text-sm">Combo price</span><span data-testid="combo-price" className="font-serif-display text-5xl">{inr(COMBO.pair)}</span>
          </div>
          <p className="mt-2 text-xs text-[#EAE0D5]/75">Exact combo price – coupons and the UPI 10% discount don't apply to combo items.</p>
          <button data-testid="add-combo-to-bag-button" disabled={!ready} onClick={addCombo} className="btn-light mt-6 w-full disabled:opacity-50"><ShoppingBag className="h-4 w-4" /> {ready ? "Add Combo to Bag" : `Pick ${picks.filter((x) => !x).length} more style${picks.filter((x) => !x).length > 1 ? "s" : ""}`}</button>
        </aside>
      </div>

      <h2 className="mb-6 mt-16 font-serif-display text-3xl font-semibold">Choose from {items.length} combo styles</h2>
      <div data-testid="combo-picker-grid" className="grid grid-cols-2 gap-3 sm:gap-6 lg:grid-cols-4">
        {items.map((p) => {
          const n = picks.filter((x) => x?.p.slug === p.slug).length;
          return (
            <div key={p.slug} data-testid={`combo-option-${p.slug}`} className={`overflow-hidden rounded-2xl border bg-white transition-shadow hover:shadow-lg ${n ? "border-[#701A24] ring-1 ring-[#701A24]" : "border-[#E7E2DB]"}`}>
              <Link to={`/products/${p.slug}`} className="block"><img src={p.images[0]} alt={p.alt} loading="lazy" className="aspect-[3/4] w-full object-cover object-top" /></Link>
              <div className="p-3.5">
                <p className="line-clamp-2 text-sm font-medium leading-snug">{p.name}</p>
                <p className="mt-1 text-sm font-semibold">{inr(p.price)}</p>
                <button data-testid={`combo-pick-${p.slug}`} onClick={() => pick(p)} className={`mt-3 flex w-full items-center justify-center gap-1.5 rounded-full py-2.5 text-xs font-semibold transition-colors ${n ? "bg-[#701A24] text-white" : "border border-[#701A24] text-[#701A24] hover:bg-[#701A24] hover:text-white"}`}>
                  {n ? <><Check className="h-3.5 w-3.5" /> Picked{n > 1 ? " ×2" : ""}{ready ? "" : " · Add again"}</> : <><Plus className="h-3.5 w-3.5" /> Pick for combo</>}
                </button>
              </div>
            </div>
          );
        })}
      </div>
      {ready && (
        <div data-testid="combo-mobile-bar" className="fixed inset-x-0 bottom-0 z-40 flex items-center gap-3 border-t border-[#E7E2DB] bg-[#FAF7F2]/95 p-3 backdrop-blur-md lg:hidden">
          <span className="font-serif-display text-2xl text-[#701A24]">{inr(COMBO.pair)}</span>
          <button data-testid="combo-mobile-add-button" onClick={addCombo} className="btn-primary flex-1 px-3"><ShoppingBag className="h-4 w-4" /> Add Combo to Bag</button>
        </div>
      )}
    </div>
  );
}
